/**
 * Colour tokens for the admin content area (everything rendered inside the
 * shell tile, plus the login page). The shell — sidebar, phone app bar,
 * account menu — uses `theme/createAppTheme.ts` and never sees these tokens.
 *
 * Admin red is the only accent and is always a flat, solid fill (no gradients).
 * Semantic colours follow their meaning: success / completed is green, pending is
 * yellow, warnings orange, failures red, always with a text label. Green is kept
 * to those meanings: `contentColors.test.ts` rejects any other token with a hue
 * between 75° and 175° (accent, charts, borders, categories).
 */

export type AppColorMode = 'light' | 'dark';

export const CONTENT_PALETTE = {
  light: {
    primary: { main: '#DC2626', light: '#EF4444', dark: '#B91C1C', contrastText: '#FFFFFF' },
    secondary: { main: '#334155', light: '#475569', dark: '#0F172A', contrastText: '#FFFFFF' },
    success: { main: '#15803D', light: '#16A34A', dark: '#166534', contrastText: '#FFFFFF' },
    warning: { main: '#B45309', light: '#F59E0B', dark: '#92400E', contrastText: '#FFFFFF' },
    error: { main: '#C62828', light: '#D64545', dark: '#9B1C1C', contrastText: '#FFFFFF' },
    info: { main: '#334155', light: '#475569', dark: '#1E293B', contrastText: '#FFFFFF' },
    text: { primary: '#0F172A', secondary: '#475569', disabled: '#64748B' },
    background: {
      default: '#F1F5F9',
      paper: '#FFFFFF',
      elevated: '#F8FAFC',
      input: '#FFFFFF',
      inputFocused: '#FFFFFF',
      shell: '#FFFFFF',
    },
  },
  dark: {
    primary: { main: '#F87171', light: '#FCA5A5', dark: '#EF4444', contrastText: '#0F172A' },
    secondary: { main: '#CBD5E1', light: '#E2E8F0', dark: '#94A3B8', contrastText: '#0F172A' },
    success: { main: '#4ADE80', light: '#86EFAC', dark: '#22C55E', contrastText: '#0F172A' },
    warning: { main: '#F0B45A', light: '#F6CD8E', dark: '#C98A2E', contrastText: '#0F172A' },
    error: { main: '#F87171', light: '#FCA5A5', dark: '#DC2626', contrastText: '#0F172A' },
    info: { main: '#CBD5E1', light: '#E2E8F0', dark: '#94A3B8', contrastText: '#0F172A' },
    text: { primary: '#F8FAFC', secondary: '#CBD5E1', disabled: '#94A3B8' },
    background: {
      default: '#0F172A',
      paper: '#1E293B',
      elevated: '#273449',
      input: '#1E293B',
      inputFocused: '#273449',
      shell: '#1E293B',
    },
  },
} as const;

export const CONTENT_BORDERS = {
  light: {
    subtle: 'rgba(15, 23, 42, 0.08)',
    default: 'rgba(15, 23, 42, 0.14)',
    strong: 'rgba(15, 23, 42, 0.28)',
    rule: '#0F172A',
  },
  dark: {
    subtle: 'rgba(255, 255, 255, 0.08)',
    default: 'rgba(255, 255, 255, 0.14)',
    strong: 'rgba(255, 255, 255, 0.26)',
    rule: '#F8FAFC',
  },
} as const;

export const CONTENT_ACTION = {
  light: {
    hover: 'rgba(15, 23, 42, 0.04)',
    selected: 'rgba(220, 38, 38, 0.08)',
    focusRing: 'rgba(220, 38, 38, 0.24)',
  },
  dark: {
    hover: 'rgba(255, 255, 255, 0.06)',
    selected: 'rgba(248, 113, 113, 0.18)',
    focusRing: 'rgba(248, 113, 113, 0.32)',
  },
} as const;

/**
 * Muted, mutually distinct series colours with no greens (red, slate, ochre,
 * steel, clay, slate-blue, rose, rust). The first series is admin red, the
 * second slate so two-series charts never read as red-on-red.
 * Composition charts use `accentRamp` (one hue) instead.
 */
export const CONTENT_CHART_COLORS = {
  light: ['#DC2626', '#334155', '#B07A1E', '#3D5A80', '#8C6D5A', '#7A869A', '#BE185D', '#A8442A'],
  dark: ['#F87171', '#CBD5E1', '#E0A04A', '#8FB0D6', '#C4A48E', '#A3AEC2', '#F472B6', '#F0A07A'],
} as const;

/** Architectural radius scale (px) for the content area. */
export const CONTENT_SHAPE = {
  /** chips, tooltips, skeletons, bars */
  xs: 4,
  /** buttons, inputs, alerts, icon squares */
  sm: 6,
  /** panels, cards, dialogs, map frames */
  md: 8,
} as const;
